import * as yup from "yup";
import { useTranslation } from "react-i18next";

export const appl8130ValidationSchema = (submissionDate: Date) => {
  const { t } = useTranslation();
  const minDate = new Date("1900-01-01");
  const percentageRegex =
    /^((0(\.[0-9]+)?|1)|([0-9]+\/[1-9]+\d*)|(100|[0-9]?[0-9](\.[0-9]+)?)%)$/;

  return yup
    .object()
    .shape({
      coYearsToClose: yup
        .string()
        .nullable()
        .max(
          4,
          t(
            "Το πεδίο Διάρκεια εταιρείας μπορεί να περιλαμβάνει μέχρι 4 χαρακτήρες.",
          ),
        )
        .test(
          "is-positive-integer",
          t("Το πεδίο πρέπει να είναι θετικός ακέραιος και όχι 0."),
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            } else {
              return /^[1-9]\d*?$/.test(value.toString());
            }
          },
        )
        .when("coAoristi", {
          is: true,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.nullable(),
        }),
      d50_id: yup.object().required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocStatuteSigned: yup
        .string()
        .required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocStatute: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      docStatuteDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900")),
      contractNumber: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      contractDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900")),
      contracterName: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      contracterSurname: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      coUsageYearStart: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable()
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900")),
      coUsageYearEnd: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable()
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900")),
      coEndDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable()
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900")),
      coRegion: yup
        .string()
        .max(50, t("Το πεδίο πρέπει να είναι μέχρι 50 χαρακτήρες"))
        .nullable(),
      coCity: yup
        .string()
        .max(50, t("Το πεδίο πρέπει να είναι μέχρι 50 χαρακτήρες"))
        .nullable(),
      coZipCode: yup
        .string()
        .max(
          12,
          t(
            "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
          ),
        )
        .nullable(),
      changedArticles: yup.array().of(
        yup.object().shape({
          editedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      deletedArticles: yup.array().of(
        yup.object().shape({
          deletedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      newArticles: yup.array().of(
        yup.object().shape({
          addedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      newBoardMembers: yup.array().of(
        yup.object().shape({
          fp1Percentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
          fp1Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp1SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp1JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Name: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Surname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Fathersname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Doy: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Adt: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.max(20).required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Odos: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Arithm: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Tk: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Polh: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2BirthCity: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          fp2BirthYear: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null))
                .test(
                  "four-decimal-year",
                  t("Ελέγξτε την ορθότητα των στοιχείων."),
                  (value) =>
                    value === undefined ||
                    value === null ||
                    /^\d{4}?$/.test(value.toString()),
                ),
          }),
          fp2Percentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
        }),
      ),
      fp2Idiot: yup.object().when(["ok"], {
        is: (ok: string) => ok === "no",
        then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
      }),
      fp2SoloRepresentative: yup.number().when(["ok"], {
        is: (ok: string) => ok === "no",
        then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
      }),
      fp2JointlyRepresentative: yup.number().when(["ok"], {
        is: (ok: string) => ok === "no",
        then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
      }),
      newLegalEntityMembers: yup.array().of(
        yup.object().shape({
          partnerCompPercentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
          partnerCompIdiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompSoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompJointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompGemi2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          partnerCompName2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompDoy2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          partnerCompArithm2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          partnerCompPolh2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          partnerCompDhmos2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .shape({
                  label: yup.string(),
                  value: yup.string(),
                })
                .nullable()
                .notRequired()
                .default(undefined)
                .transform((value, originalValue) => {
                  if (
                    originalValue &&
                    typeof originalValue === "object" &&
                    Object.keys(originalValue).length === 0
                  ) {
                    return undefined;
                  }
                  return value;
                }),
          }),
          partnerCompPercentage2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
          partnerCompIdiot2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompSoloRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompJointlyRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompTk2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          newRepresentatives: yup.array().of(
            yup.object().shape({
              fp2CompReprName: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprSurname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprFathersname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprDoy: yup.object().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprAdt: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprOdos: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprArithm: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprTk: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      12,
                      t(
                        "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprBirthCity: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .nullable()
                    .notRequired()
                    .transform((value) => (!!value ? value : null)),
              }),
              fp2CompReprPolh: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprBirthYear: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .nullable()
                    .notRequired()
                    .transform((value) => (!!value ? value : undefined))
                    .test(
                      "four-decimal-year",
                      t("Ελέγξτε την ορθότητα των στοιχείων."),
                      (value) =>
                        value === undefined ||
                        value === null ||
                        /^\d{4}?$/.test(value.toString()),
                    ),
              }),
            }),
          ),
        }),
      ),
      newBoardManagers: yup.array().of(
        yup.object().shape({
          manFp1EndDate: yup.date().when(["ok", "manFp1EndDateAoristi"], {
            is: (ok: string, manFp1EndDateAoristi: string | undefined) =>
              ok === "ok" && manFp1EndDateAoristi === undefined,
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manFp1EndDateAoristi: yup.string(),
          manFp1Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp1SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp1JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Name: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Surname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Fathersname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Doy: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Adt: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Odos: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Arithm: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Polh: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Tk: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2BirthCity: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2BirthYear: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .test(
                  "four-decimal-year",
                  t("Ελέγξτε την ορθότητα των στοιχείων."),
                  (value) =>
                    value === undefined ||
                    value === null ||
                    /^\d{4}?$/.test(value.toString()),
                ),
          }),
          manFp2EndDate: yup.date().when(["ok", "manFp2EndDateAoristi"], {
            is: (ok: string, manFp2EndDateAoristi: string | undefined) => {
              return ok === "no" && manFp2EndDateAoristi === undefined;
            },
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manFp2EndDateAoristi: yup.string(),
          manFp2Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
        }),
      ),
      newPartnerManagers: yup.array().of(
        yup.object().shape({
          manCompEndDate: yup.date().when(["ok", "manCompEndDateAoristi"], {
            is: (ok: string, manCompEndDateAoristi: string | undefined) =>
              ok === "ok" && manCompEndDateAoristi === undefined,
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manCompEndDateAoristi: yup.string(),
          manCompIdiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompSoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompJointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompGemi2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompName2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompDoy: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .nullable()
                .notRequired()
                .default(undefined)
                .transform((value, originalValue) => {
                  if (
                    originalValue &&
                    typeof originalValue === "object" &&
                    Object.keys(originalValue).length === 0
                  ) {
                    return undefined;
                  }
                  return value;
                }),
          }),
          manCompOdos: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          manCompArithm: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          manCompPolh: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          manCompDhmos: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .shape({
                  label: yup.string(),
                  value: yup.string(),
                })
                .nullable()
                .notRequired()
                .default(undefined)
                .transform((value, originalValue) => {
                  if (
                    originalValue &&
                    typeof originalValue === "object" &&
                    Object.keys(originalValue).length === 0
                  ) {
                    return undefined;
                  }
                  return value;
                }),
          }),
          manCompEndDate2: yup.date().when(["ok", "manCompEndDateAoristi2"], {
            is: (ok: string, manCompEndDateAoristi2: string | undefined) =>
              ok === "no" && manCompEndDateAoristi2 === undefined,
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manCompEndDateAoristi2: yup.string(),
          manCompIdiot2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompTk: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .nullable()
                .notRequired()
                .transform((value) => (!!value ? value : null)),
          }),
          manCompSoloRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompJointlyRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          newRepresentativeManagers: yup.array().of(
            yup.object().shape({
              fp2CompReprName: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprSurname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprFathersname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprDoy: yup.object().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprAdt: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprOdos: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprArithm: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprPolh: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprTk: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      12,
                      t(
                        "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprBirthCity: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .nullable()
                    .notRequired()
                    .transform((value) => (!!value ? value : null)),
              }),
              fp2CompReprBirthYear: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .nullable()
                    .notRequired()
                    .transform((value) => (!!value ? value : null))
                    .test(
                      "four-decimal-year",
                      t("Ελέγξτε την ορθότητα των στοιχείων."),
                      (value) =>
                        value === undefined ||
                        value === null ||
                        /^\d{4}?$/.test(value.toString()),
                    ),
              }),
            }),
          ),
        }),
      ),
      i21_capitalstock: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      i19_shareamount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      i19_nominalprice: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      shareNum: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      capital: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      shareValue: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
    })
    .test("capital-match", function (value) {
      const { shareNum, shareValue, capital } = value;
      if (shareNum || shareValue) {
        if (!shareNum) {
          return this.createError({
            message: t(
              "Πρέπει να συμπληρώσετε το Πλήθος Μεριδίων εφόσον έχει συμπληρωθεί η Ονομαστική Αξία Μεριδίου.",
            ),
          });
        }

        if (!shareValue) {
          return this.createError({
            message: t(
              "Πρέπει να συμπληρώσετε την Ονομαστική Αξία Μεριδίου εφόσον έχει συμπληρωθεί το Πλήθος Μεριδίων.",
            ),
          });
        }
      }
      if (capital && shareNum && shareValue) {
        if (
          parseFloat(capital || "0") !==
          parseFloat(shareNum || "0") * parseFloat(shareValue || "0")
        ) {
          return this.createError({
            message: t("Το Κεφάλαιο δεν ταιριάζει με τα στοιχεία μεριδίων."),
          });
        }
      }
      return true;
    })
    .test(
      "at-least-one-article",
      t("Πρέπει να εισάγετε αναλυτικά τις τροποποιήσεις του καταστατικού."),
      (value) =>
        (value.changedArticles && value.changedArticles.length > 0) ||
        (value.deletedArticles && value.deletedArticles.length > 0) ||
        (value.newArticles && value.newArticles.length > 0),
    );
};
